import type { Node } from '@xyflow/react';
import NodeData, {
  NodeDataType,
  type NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';
import type { NodeModel } from 'src/modules/network-configuration/models/node-model';
import { nodeScale } from 'src/modules/network-configuration/models/node-utils';

export const RESERVED_AUTO_DEPLOY_VLAN = 3579;
export const AUTO_DEPLOY_IP = '172.31.255.0';

export const VLANS_COLORS = [
  '#3B82F6', // blue
  '#EF4444', // red
  '#10B981', // green
  '#F59E0B', // amber
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#F97316', // orange
  '#6366F1', // indigo
  '#14B8A6', // teal
  '#E11D48', // rose
  '#A855F7', // purple
  '#22C55E', // green bright
  '#0EA5E9', // sky
  '#D97706', // amber dark
  '#DC2626', // red dark
  '#4F46E5', // indigo dark
  '#059669', // emerald dark
  '#9333EA', // purple dark
  '#0284C7', // sky dark
  '#7C3AED', // violet strong
  '#BE185D', // pink dark
  '#15803D', // green deep
  '#B45309', // orange brown
  '#1D4ED8', // blue deep
  '#C026D3', // fuchsia
  '#0F766E', // teal deep
  '#9F1239', // rose deep
  '#334155', // slate (neutral)
];

export const buildNode = ({
  id,
  nodeType,
  category,
  position,
}: {
  id: string;
  nodeType: NodeDataType;
  category: NodeCategoryModel;
  position?: { x: number; y: number };
}): Node<NodeModel> => ({
  id,
  type: nodeType,
  position: position || { x: 0, y: 0 },
  width: category.resizable ? category.viewSize.width * nodeScale : undefined,
  height: category.resizable ? category.viewSize.height * nodeScale : undefined,
  data: {
    key: nodeType,
    label: NodeData[nodeType]?.name || category.name,
    category,
  },
});

export const normalizeName = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, ' ');

export const getPrimaryPortId = (category: NodeCategoryModel) =>
  Object.keys(category.portIndexes || {})[0] ?? '';

/** First SOURCE port id for a device node (e.g. `camera`, not `generic-device`). */
export const getDeviceSourcePortId = (
  category: NodeCategoryModel,
  nodeType: NodeDataType,
) =>
  getPrimaryPortId(category) ||
  NodeData[nodeType]?.ports?.[0]?.id ||
  NodeData[NodeDataType.GenericDevice].ports?.[0]?.id ||
  'generic-device';

export const getEdgeKey = (
  sourceNodeId: string,
  sourcePortId: string,
  targetNodeId: string,
  targetPortId: string,
) =>
  [`${sourceNodeId}__${sourcePortId}`, `${targetNodeId}__${targetPortId}`]
    .sort()
    .join('::');

/**
 * Same rule as legacy `build-imported-topology`: logical edge is
 * localSwitch__port__source → peer__port__target; if either handle id does not
 * start with `006`, swap handles and flip React Flow source/target.
 */
export const finalizeImportedEdgeEndpoints = (
  logicalLocalSwitchId: string,
  logicalLocalSwitchPortId: string,
  logicalPeerId: string,
  logicalPeerPortId: string,
) => {
  let sourceHandle = `${logicalLocalSwitchId}__${logicalLocalSwitchPortId}__source`;
  let targetHandle = `${logicalPeerId}__${logicalPeerPortId}__target`;
  let source = logicalLocalSwitchId;
  let target = logicalPeerId;

  if (!sourceHandle.startsWith('006') || !targetHandle.startsWith('006')) {
    const newSourceHandle = targetHandle;
    targetHandle = sourceHandle.replace('__source', '__target');
    sourceHandle = newSourceHandle.replace('__target', '__source');
    source = logicalPeerId;
    target = logicalLocalSwitchId;
  }

  return { source, target, sourceHandle, targetHandle };
};

export const getEdgeId = (sourceHandle: string, targetHandle: string) =>
  `${sourceHandle.replace('__source', '')}____${targetHandle.replace('__target', '')}`;
